/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import React from 'react';
import dayjs from 'dayjs';

import { DateInput, Form, Grid } from '@mairistem/composants';
import { AdresseForm } from '@mairistem/adresses';

import { SectionHeader } from '@/components/Common';
import { useAdresseFormFiche } from '@/hooks';

import PanelModalEdition from '../../PanelModalEdition';
import CommuneDelegueField from '../CommuneDelegueField';

interface InformationsConventionSignatureModalProps {
  data: any;
  onSave: () => Promise<void>;
  onChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
  onCancel: () => void;
}

const InformationsConventionSignatureModal = (
  props: InformationsConventionSignatureModalProps,
) => {
  const { data, onSave, onChange, onClose, onCancel } = props;

  const [loading, setLoading] = React.useState<boolean>(false);
  const [errors, setErrors] = React.useState<
    Record<string, string | boolean> & { api?: boolean }
  >({});

  const handleChangeDate = React.useCallback(
    (date: string | Date | undefined) => {
      onChange({
        dateConvention: date ? dayjs(date).format('YYYY-MM-DD') : null,
      });
    },
    [onChange],
  );

  const handleAdresseChange = React.useCallback(
    (adresse: Record<string, unknown>) =>
      onChange({
        adresseConvention: { ...data?.adresseConvention, ...adresse },
      }),
    [onChange, data?.adresseConvention],
  );
  const adresseForm = useAdresseFormFiche(
    data?.adresseConvention,
    handleAdresseChange,
  );

  const handleChangeCommuneDelegue = React.useCallback(
    (libelle: string, drapeau: boolean) => {
      onChange({
        adresseConvention: {
          ...data?.adresseConvention,
          communeDelegue: libelle,
          estCommuneDelegue: drapeau,
        },
      });
    },
    [onChange, data?.adresseConvention],
  );

  const handleSave = React.useCallback(() => {
    setLoading(true);

    onSave()
      .then(() => {
        onClose();
      })
      .catch(() => {
        setErrors({ api: true });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [onClose, onSave]);

  return (
    <PanelModalEdition
      data-testid="convention-signature-modal"
      errors={errors}
      loading={loading}
      header="SIGNATURE DE LA CONVENTION"
      onSave={handleSave}
      onClose={onCancel}
    >
      <Form>
        <Grid>
          <Grid.Row>
            <Grid.Column width={16}>
              <SectionHeader title="SIGNATURE" style={{ marginTop: 0 }} />
              <Form.Field
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  margin: '1em',
                }}
              >
                <label>Convention signée le</label>
                <DateInput
                  data-testid="convention-signature-date"
                  value={
                    data?.dateConvention
                      ? new Date(data.dateConvention)
                      : undefined
                  }
                  onChange={handleChangeDate}
                />
              </Form.Field>
              <SectionHeader
                title="LIEU DE SIGNATURE"
                style={{ marginTop: 0 }}
              />
              <div style={{ margin: '1em' }}>
                <AdresseForm
                  saisie="libre"
                  handleTypes
                  displayDepartement={false}
                  displayArticle
                  adresse={adresseForm.adresseFormValue}
                  onItemChange={adresseForm.onItemChange}
                  onChange={adresseForm.onChange}
                />
                {/* Même espacement (2em) qu'entre les lignes d'AdresseForm */}
                <div style={{ marginTop: '2em' }}>
                  <CommuneDelegueField
                    data-testid="convention-commune-delegue"
                    libelle={data?.adresseConvention?.communeDelegue}
                    drapeau={data?.adresseConvention?.estCommuneDelegue}
                    onChange={handleChangeCommuneDelegue}
                  />
                </div>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
      </Form>
    </PanelModalEdition>
  );
};

export default InformationsConventionSignatureModal;
